import { useState } from 'react'
import { ChevronDown, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'

interface City {
  id: string
  name: string
  country: string
  coordinates: { lat: number; lng: number }
}

const cities: City[] = [
  { id: 'cairo', name: 'القاهرة', country: 'مصر', coordinates: { lat: 30.0444, lng: 31.2357 } },
  { id: 'riyadh', name: 'الرياض', country: 'السعودية', coordinates: { lat: 24.7136, lng: 46.6753 } },
  { id: 'dubai', name: 'دبي', country: 'الإمارات', coordinates: { lat: 25.2048, lng: 55.2708 } },
  { id: 'jeddah', name: 'جدة', country: 'السعودية', coordinates: { lat: 21.4858, lng: 39.1925 } },
  { id: 'mecca', name: 'مكة المكرمة', country: 'السعودية', coordinates: { lat: 21.3891, lng: 39.8579 } },
  { id: 'madina', name: 'المدينة المنورة', country: 'السعودية', coordinates: { lat: 24.5247, lng: 39.5692 } },
]

interface CitySelectorProps {
  selectedCity: City | null
  onCityChange: (city: City) => void
}

export default function CitySelector({ selectedCity, onCityChange }: CitySelectorProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const defaultCity = cities[0] // Cairo

  const handleSelect = (city: City) => {
    onCityChange(city)
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center justify-between w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg',
          'hover:border-primary-500 dark:hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent',
          'transition-colors text-gray-900 dark:text-gray-100'
        )}
        aria-label={t('prayer.selectCity')}
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3">
          <MapPin className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <div className="text-right">
            <div className="font-medium text-gray-900 dark:text-gray-100">
              {selectedCity?.name || defaultCity.name}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{selectedCity?.country || defaultCity.country}</div>
          </div>
        </div>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform',
            isOpen && 'transform rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute top-full mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
            {cities.map((city) => (
              <button
                key={city.id}
                onClick={() => handleSelect(city)}
                className={cn(
                  'w-full text-right px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors',
                  selectedCity?.id === city.id && 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                )}
              >
                <div className="font-medium text-gray-900 dark:text-gray-100">{city.name}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{city.country}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

